import React from 'react'

import { Toggle } from '../../common/components/Toggle'
import { ChargePoint, Connector, GreenChargingChargePointStatus } from '../../common/redux/api/web-api'
import Text from '../../ui/Text'
import { ClockIcon } from '../icons/clock-icon'
import { RemoteCapiNfcControl } from '../remote-capi-nfc-control'
import { RemoteControlRow } from '../remote-control-row'
import { toggleConnectorAvailabilityStatus } from '../toggle-connector-availability-status'
import ConnectorStatus from '../connector-status'
import useText from '../../common/hooks/useText'

export type CapiAuraConnectorControlProps = {
    connector: Connector
    chargePoint: ChargePoint
    applyChargePoint: (chargePoint: ChargePoint) => Promise<void>
    isReadOnly: boolean
    isOnline: boolean
    greenChargingStatus?: GreenChargingChargePointStatus['greenChargingStatus']
}

export function CapiAuraConnectorControl({
    connector,
    chargePoint,
    applyChargePoint,
    greenChargingStatus,
    isOnline,
    isReadOnly,
}: CapiAuraConnectorControlProps): React.ReactElement {
    const text = useText()
    const toggleCableLock = async () => {
        const tempChargePoint = {
            ...chargePoint,
            connectors: chargePoint.connectors?.map((connectorItem) => {
                if (connectorItem.connectorId !== connector.connectorId) return connectorItem
                return { ...connectorItem, cableLocked: !connector.cableLocked }
            }),
        }
        await applyChargePoint(tempChargePoint)
    }

    const applyConnectorAvailabilityStatus = async (chargingConnector: Connector) => {
        await applyChargePoint(
            toggleConnectorAvailabilityStatus(chargingConnector, !!greenChargingStatus?.greenChargingOn, chargePoint)
        )
    }

    return (
        <>
            <RemoteControlRow
                title={
                    <div data-testid={`charger-${chargePoint.id}-connector-${connector.connectorId}`}>
                        {text(connector.connectorId === 1 ? 'lblLeftSocket' : 'lblRightSocket')}
                    </div>
                }
                button={
                    connector.mode !== 'Schedule' ? (
                        <Toggle
                            toggleText={text(connector.mode === 'On' ? 'btnSocketIsOn' : 'btnSocketIsOff')}
                            isToggled={connector.onByUser}
                            disabled={isReadOnly}
                            toggleFn={() => applyConnectorAvailabilityStatus(connector)}
                            data-testid={`toggle-charger-${chargePoint.id}-connector-${connector.connectorId}`}
                        />
                    ) : (
                        <Toggle
                            toggleText={text(
                                connector.mode === 'Schedule' ? 'btnChargerIsOnBySchedule' : 'btnChargerIsOffBySchedule'
                            )}
                            isToggled={connector.onBySchedule}
                            toggleFn={() => applyConnectorAvailabilityStatus(connector)}
                            icon={<ClockIcon size={18} />}
                            disabled={isReadOnly}
                            data-testid={`toggle-chargerScheduling-${chargePoint.id}-connector-${connector.connectorId}`}
                        />
                    )
                }
                status={
                    <>
                        {isOnline && (
                            <ConnectorStatus
                                connector={connector}
                                isOcpp={chargePoint?.isOcpp}
                                key={connector?.connectorId}
                                showConsumption={true}
                            />
                        )}
                    </>
                }
            />
            <RemoteCapiNfcControl
                chargePoint={chargePoint}
                selectedConnector={connector}
                isReadOnly={isReadOnly}
                applyChargePoint={applyChargePoint}
            />
            <RemoteControlRow
                title={
                    <div data-testid={`calbeLockTitle-${chargePoint.id}-connector-${connector.connectorId}`}>
                        <Text code="lblCableLock" />
                    </div>
                }
                button={
                    <Toggle
                        isToggled={connector.cableLocked}
                        toggleFn={() => toggleCableLock()}
                        disabled={isReadOnly}
                        data-testid={`toggle-cablelock-${chargePoint.id}-connector-${connector.connectorId}`}
                    />
                }
            />
        </>
    )
}
